import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { ISegment, TPredicateMode } from '../segments';
import type { ITargetable } from '../targetables';
import type { ITargeting } from '../targetings';
import { DisplayCampaign } from './display';
import type { IMonetizationAttributes } from './monetization';
import type { ICampaignAttributes } from './types';
import { CampaignClass } from './types';

export enum EBiddingStrategyClass {
  /** User selected auto-optimize for backend to auto optimize the bidding strategy after publish */
  Auto = 'Auto',
  /** User selected customize to provide specific bidding strategy values */
  Manual = 'Manual',
}

export enum ECampaignObjective {
  Conversions = 'conversions',
  Reach = 'reach',
  Custom = 'custom',
  HighDemand = 'high_demand',
}

export interface IBiddingStrategy {
  _cls: EBiddingStrategyClass;
  base_bid: number;
  /** If the user saved a non-default bid value */
  custom_bid: boolean;
  /**
   * To enable cross device, pass undefined on the frontend so the backend falls back to the
   * right vendor on save. Passing 0 turns off cross device for the given campaign.
   *
   * Cross device should never be enabled for monetization campaigns */
  cross_device_vendor_id?: number;
  /**
   * Maximum bid capped at 2x base bid.
   * If "legacy" (published and the initial bid is under $5) it's 3x base bid.
   */
  max_bid: number;
  /** The amount of times in the frequency period that a user can see the ad */
  freq_cap: number;
  /** The amount of time (in minutes) before the frequency cap for a user resets */
  freq_period: number;
  /** Tradedesk feature that lowers bid price based on predicting bid competition */
  readonly predictive_clearing?: boolean;
}

export interface ITTDCampaignAttributes extends ICampaignAttributes, IMonetizationAttributes {
  readonly _cls:
    | CampaignClass.Segment
    | CampaignClass.Lookalike
    | CampaignClass.SeedSegment
    | CampaignClass.Affinity
    | CampaignClass.Search
    | CampaignClass.EmailList
    | CampaignClass.MobileGeoFencing
    | CampaignClass.MobileGeoFenceRetargeting
    | CampaignClass.Omni;
  bidding_strategy: IBiddingStrategy;
  destination_url: string;
  mode: TPredicateMode;
  objective: ECampaignObjective;
}

export interface IEphemeralTargetableOrSegment extends Omit<ITargeting, 'target_data'> {
  target_data: Partial<ITargetable> | Partial<ISegment> | undefined;
}

interface IRecommendedBudgetPayload {
  campaign: Partial<ICampaignAttributes>;
  targetings: IEphemeralTargetableOrSegment[];
}

/**
 * Per-channel and total dollar amounts returned by the budget recommendation
 * endpoint. `total` is the sum across active channels; `display`/`meta` are the
 * per-channel amounts over the full campaign duration (not daily).
 */
/** Aggregated per-channel + total budgets used internally by the ad wizard. */
export interface IChannelBudgets {
  display: number;
  meta: number;
  total: number;
}

/** Per-channel split as whole-number percentages summing to 100. */
export interface IRecommendedSplit {
  display: number;
  meta: number;
}

/** Per-channel breakdown returned by the budget recommendation endpoint. */
export interface IChannelBudgetBreakdown {
  /** Dollars allocated to the channel at the current budget. */
  amount: number;
  /** Whole-number percentage of the total budget. */
  percent: number;
  /** Per-channel minimum budget in dollars. */
  minimum: number;
}

interface IRecommendedBudgetResponse extends IBaseAttributes {
  recommended_budget: number;
  current_budget: number;
  minimum_budget: number;
  /** True when the current budget is below the minimum required for the campaign. */
  budget_below_minimum: boolean;
  channels: {
    display: IChannelBudgetBreakdown;
    meta: IChannelBudgetBreakdown;
  };
}

export const REACH_CAMPAIGNS = [
  CampaignClass.Affinity,
  CampaignClass.SeedSegment,
  CampaignClass.Lookalike,
  CampaignClass.Search,
  CampaignClass.EmailList,
  CampaignClass.MobileGeoFencing,
  CampaignClass.MobileGeoFenceRetargeting,
];

export const HIGH_DEMAND_CAMPAIGNS = [
  CampaignClass.Omni,
  CampaignClass.Segment,
  CampaignClass.EmailList,
];

export const CONVERSIONS_CAMPAIGNS = [
  CampaignClass.Omni,
  CampaignClass.Segment,
  CampaignClass.EmailList,
];

export const CUSTOM_CAMPAIGNS = [
  CampaignClass.Affinity,
  CampaignClass.Segment,
  CampaignClass.SeedSegment,
  CampaignClass.Lookalike,
  CampaignClass.Search,
  CampaignClass.EmailList,
  CampaignClass.MobileGeoFencing,
  CampaignClass.MobileGeoFenceRetargeting,
];

export abstract class TTDCampaign<
  T extends ITTDCampaignAttributes = ITTDCampaignAttributes,
> extends DisplayCampaign<T> {
  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      objective: {
        presence: {
          allowEmpty: false,
        },
      },
    };
  }

  public static override readonly defaults: Partial<ITTDCampaignAttributes> &
    Pick<Required<ITTDCampaignAttributes>, 'bidding_strategy'> = {
    bidding_strategy: {
      _cls: EBiddingStrategyClass.Auto,
      base_bid: 5,
      custom_bid: false,
      max_bid: 10,
      freq_cap: 9,
      freq_period: 480,
    },
    mode: 'match_any',
  };

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      ...TTDCampaign.defaults,
    } as Partial<T>;
  }

  /**
   * Returns which objectives are available/enabled for this campaign type
   * This logic is shared between the campaign creation modal and the optimization step
   */
  public getAvailableObjectives(flags?: { isPlatformLite?: boolean }): IObjectiveAvailability {
    const campaignClass = this.get('_cls');
    return getObjectiveAvailabilityForCampaignClass(campaignClass, flags);
  }

  public async getRecommendedBudget(
    payload?: IRecommendedBudgetPayload,
  ): Promise<IRecommendedBudgetResponse> {
    this.assertCollection(this.collection);

    const url = `${this.collection.url()}${this.id}/budget_recommendation`;

    const response = await wretch<IRecommendedBudgetResponse>(url, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: this.collection.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }
}

export interface IObjectiveAvailability {
  reach: boolean;
  high_demand: boolean;
  conversions: boolean;
  custom: boolean;
}

/**
 * Determines if a campaign class is a TTD campaign type that should show the objective step
 */
export function isTTDCampaignWithObjective(campaignClass: CampaignClass): boolean {
  const ttdCampaignTypes = [
    // Retargeting
    CampaignClass.Segment,
    CampaignClass.Affinity,
    // Email Mapping
    CampaignClass.EmailList,
    // Search Keyword
    CampaignClass.Search,
    // Lookalike
    CampaignClass.SeedSegment,
    // Historical Geofencing
    CampaignClass.MobileGeoFenceRetargeting,
    // Mobile Geofencing
    CampaignClass.MobileGeoFencing,
    // Pixl Plus (Omni)
    CampaignClass.Omni,
  ];

  return ttdCampaignTypes.includes(campaignClass);
}

/**
 * Returns objective availability for a campaign class without needing a campaign instance
 */
export function getObjectiveAvailabilityForCampaignClass(
  campaignClass: CampaignClass,
  flags?: { isPlatformLite?: boolean },
): IObjectiveAvailability {
  let reachEnabled = REACH_CAMPAIGNS.includes(campaignClass);
  const highDemandEnabled = HIGH_DEMAND_CAMPAIGNS.includes(campaignClass);
  const conversionsEnabled = CONVERSIONS_CAMPAIGNS.includes(campaignClass);
  const customEnabled = CUSTOM_CAMPAIGNS.includes(campaignClass);

  // Enable reach objective for Omni campaigns (unless Platform Lite)
  if (!flags?.isPlatformLite && campaignClass === CampaignClass.Omni) {
    reachEnabled = true;
  }

  return {
    reach: reachEnabled,
    high_demand: highDemandEnabled,
    conversions: conversionsEnabled,
    custom: customEnabled,
  };
}

export class TTDBaseCampaign extends TTDCampaign<ITTDCampaignAttributes> {}
